/**
 * EventAttachmentsSection
 *
 * Attach files (marketing PDFs / images) to an event, to share with other event owners
 * and to keep an internal record. Files live in the PRIVATE `event-attachments` bucket and
 * are read through short-lived signed URLs.
 *
 * Permission is NOT re-derived here. The list RPC gates on `event_attachment_can_manage`
 * and raises `insufficient_privilege` for anyone else, so this component simply renders
 * nothing when the server refuses. Duplicating the role rule client-side would create a
 * second copy that could drift from the one that actually enforces anything.
 *
 * Rendered as a self-contained block so it cannot interfere with saving the event: it
 * writes only to `event_attachments`, never to the `events` row.
 */

import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownTrayIcon,
  EyeIcon,
  DocumentIcon,
  PaperClipIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import { useToast } from "../ui/Toast";
import {
  EVENT_ATTACHMENT_ACCEPT,
  EVENT_ATTACHMENT_MAX_PER_EVENT,
  deleteEventAttachment,
  formatAttachmentSize,
  getEventAttachmentUrl,
  getEventAttachmentDownloadUrl,
  listEventAttachments,
  uploadEventAttachment,
  validateAttachmentFile,
  type EventAttachment,
} from "../../services/eventAttachments.service";

interface EventAttachmentsSectionProps {
  eventId: string;
  /** RM/TM (round-4 audit): they open / download the event's files (e.g. the flyer) but never upload or remove. */
  readOnly?: boolean;
}

/** PostgREST surfaces a RAISE ... insufficient_privilege as code 42501. */
const isPermissionDenied = (error: unknown): boolean => {
  const code = (error as { code?: string } | null)?.code;
  const message = (error as { message?: string } | null)?.message ?? "";
  return code === "42501" || /permission/i.test(message);
};

const readableError = (error: unknown, fallback: string): string => {
  const message = (error as { message?: string } | null)?.message;
  return message && message.trim() !== "" ? message : fallback;
};

const EventAttachmentsSection: React.FC<EventAttachmentsSectionProps> = ({
  eventId,
  readOnly = false,
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const {
    data: attachments,
    isLoading,
    error,
  } = useQuery<EventAttachment[]>({
    queryKey: ["event-attachments", eventId],
    queryFn: () => listEventAttachments(eventId),
    enabled: Boolean(eventId),
    retry: false,
  });

  // Not permitted, or the list could not be read: render nothing rather than an empty
  // panel that implies this event has no files.
  if (error) {
    if (!isPermissionDenied(error)) {
      console.error("Failed to load event attachments", error);
    }
    return null;
  }

  const items = attachments ?? [];
  const atCap = items.length >= EVENT_ATTACHMENT_MAX_PER_EVENT;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["event-attachments", eventId] });

  const handleFilesSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(e.target.files ?? []);
    // Reset immediately so re-picking the same file still fires onChange.
    e.target.value = "";
    if (files.length === 0) {
      return;
    }

    const remaining = EVENT_ATTACHMENT_MAX_PER_EVENT - items.length;
    if (remaining <= 0) {
      showToast(
        "error",
        `This event already has ${EVENT_ATTACHMENT_MAX_PER_EVENT} attachments. Remove one before adding another.`,
      );
      return;
    }

    const queued = files.slice(0, remaining);
    if (files.length > remaining) {
      showToast(
        "error",
        `Only ${remaining} more file${remaining === 1 ? "" : "s"} can be attached to this event; the rest were skipped.`,
      );
    }

    setIsUploading(true);
    let succeeded = 0;
    try {
      for (const file of queued) {
        const localError = validateAttachmentFile(file);
        if (localError) {
          showToast("error", localError);
          continue;
        }
        try {
          await uploadEventAttachment(eventId, file);
          succeeded += 1;
        } catch (uploadError) {
          showToast(
            "error",
            readableError(uploadError, `Could not attach "${file.name}".`),
          );
        }
      }
    } finally {
      setIsUploading(false);
      if (succeeded > 0) {
        await refresh();
        showToast(
          "success",
          `Attached ${succeeded} file${succeeded === 1 ? "" : "s"}.`,
        );
      }
    }
  };

  const handleOpen = async (attachment: EventAttachment) => {
    try {
      const url = await getEventAttachmentUrl(attachment.storage_path);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (openError) {
      showToast(
        "error",
        readableError(openError, "Could not open that file. Try again."),
      );
    }
  };

  // Download = save the file (Content-Disposition: attachment from storage), not open it in a tab.
  const handleDownload = async (attachment: EventAttachment) => {
    try {
      const url = await getEventAttachmentDownloadUrl(
        attachment.storage_path,
        attachment.file_name,
      );
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (downloadError) {
      showToast(
        "error",
        readableError(
          downloadError,
          "Could not download that file. Try again.",
        ),
      );
    }
  };

  const handleDelete = async (attachment: EventAttachment) => {
    const confirmed = window.confirm(
      `Remove "${attachment.file_name}" from this event?\n\nThe file is deleted permanently. A record of who attached and removed it is kept.`,
    );
    if (!confirmed) {
      return;
    }

    setDeletingId(attachment.id);
    try {
      await deleteEventAttachment(attachment.id);
      await refresh();
      showToast("success", `Removed "${attachment.file_name}".`);
    } catch (deleteError) {
      showToast(
        "error",
        readableError(deleteError, "Could not remove that file."),
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-900">Files</h3>
        <span className="text-xs text-gray-500">
          {items.length} of {EVENT_ATTACHMENT_MAX_PER_EVENT}
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-3">
        Marketing PDFs and images for this event, visible to event owners and
        administrators. PDF, PNG, JPEG, WebP or GIF, up to 25 MB each.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={EVENT_ATTACHMENT_ACCEPT}
        onChange={handleFilesSelected}
        className="hidden"
        data-testid="event-attachment-input"
      />

      {isLoading ? (
        <div className="py-4">
          <LoadingSpinner />
        </div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <PaperClipIcon className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">
            No files attached to this event yet.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
          {items.map((attachment) => {
            const isImage = attachment.mime_type.startsWith("image/");
            const Icon = isImage ? PhotoIcon : DocumentIcon;
            return (
              <li
                key={attachment.id}
                className="flex items-center gap-3 px-3 py-2"
              >
                <Icon className="h-6 w-6 flex-shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate text-sm font-medium text-gray-900"
                    title={attachment.file_name}
                  >
                    {attachment.file_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatAttachmentSize(attachment.file_size_bytes)}
                    {" · "}
                    {attachment.uploaded_by_name ?? "Unknown"}
                    {" · "}
                    {new Date(attachment.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleOpen(attachment)}
                  className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  title="View"
                  aria-label={`View ${attachment.file_name}`}
                >
                  <EyeIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(attachment)}
                  className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  title="Download"
                  aria-label={`Download ${attachment.file_name}`}
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleDelete(attachment)}
                    disabled={deletingId === attachment.id}
                    className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    title="Remove"
                    aria-label={`Remove ${attachment.file_name}`}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && (
        <div className="mt-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || atCap}
          >
            <PaperClipIcon className="h-4 w-4 mr-1.5" />
            {isUploading ? "Uploading…" : "Attach files"}
          </Button>
          {atCap && (
            <p className="mt-1 text-xs text-gray-500">
              Attachment limit reached. Remove a file to add another.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EventAttachmentsSection;
